import { createApi } from '@reduxjs/toolkit/query/react'

import { OrderDirection } from '@/constants/order-direction'
import { Period } from '@/constants/period'
import { Bot } from '@/model/bot'
import {
  infiniteOffsetPaginationQueryOptions,
  OffsetPaginationRequest,
  PaginationResponse,
} from '@/model/pagination'
import { userSlice } from '@/redux/user'
import { baseQuery } from '@/redux/utils'

export enum BotOrderField {
  UpdatedAt = 'updatedAt',
  Rating = 'rating',
  SoldCount = 'soldCount',
}

export const getBotOrderFieldLabel = (orderField: BotOrderField) => {
  switch (orderField) {
    case BotOrderField.UpdatedAt:
      return 'Last update time'
    case BotOrderField.Rating:
      return 'Rating'
    case BotOrderField.SoldCount:
      return 'Number of purchases'
  }
}

export interface ReadMarketBotsRequest {
  orderField?: BotOrderField
  orderDirection?: OrderDirection
  period?: Period
  llmId?: number
  name?: string
}

export interface RateBotRequest {
  id: number
  rating: number
}

export const readOneBot = 'readOneBot'

export const botSlice = createApi({
  reducerPath: 'bot',
  baseQuery,
  tagTypes: [readOneBot],
  endpoints: builder => ({
    readOneBot: builder.query<Bot, number>({
      query: (id) => `/bot/${id}`,
      providesTags: (_, __, id) => [{ type: readOneBot, id }],
    }),
    readMyBots: builder.query<Bot[], void>({
      query: () => '/bot/mine/all',
    }),
    readPurchasedBots: builder.query<Bot[], void>({
      query: () => '/bot/purchased/all',
    }),
    readMarketBots: builder.infiniteQuery<PaginationResponse<Bot>, ReadMarketBotsRequest, OffsetPaginationRequest>(
      {
        infiniteQueryOptions: infiniteOffsetPaginationQueryOptions,
        query: ({ queryArg, pageParam }) => ({
          url: '/bot/market/all',
          params: {
            ...queryArg,
            ...pageParam,
          },
        }),
      }),
    createBot: builder.mutation<Bot, void>({
      query: () => ({
        url: '/bot',
        method: 'POST',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled
        dispatch(botSlice.util.updateQueryData('readMyBots', undefined, (draft) => {
          draft.unshift(data)
        }))
      },
    }),
    removeBot: builder.mutation<void, number>({
      query: (id) => ({
        url: `/bot/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(botSlice.util.updateQueryData('readMyBots', undefined, (draft) => {
          return draft.filter((bot) => bot.id !== id)
        }))
        dispatch(botSlice.util.updateQueryData('readPurchasedBots', undefined, (draft) => {
          return draft.filter((bot) => bot.id !== id)
        }))
      },
    }),
    activateBot: builder.mutation<void, number>({
      query: (id) => ({
        url: `/bot/${id}/activate`,
        method: 'POST',
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(userSlice.util.updateQueryData('readMineUser', undefined, (draft) => {
          draft.activeBotId = id
        }))
      },
    }),
    deactivateBot: builder.mutation<void, number>({
      query: (id) => ({
        url: `/bot/${id}/deactivate`,
        method: 'POST',
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(userSlice.util.updateQueryData('readMineUser', undefined, (draft) => {
          draft.activeBotId = undefined
        }))
      },
    }),
    readMyRating: builder.query<number, number>({
      query: (id) => `/bot/${id}/mine/rating`,
    }),
    rateBot: builder.mutation<void, RateBotRequest>({
      query: ({ id, rating }) => ({
        url: `/bot/${id}/rate`,
        method: 'POST',
        body: { rating },
      }),
      async onQueryStarted({ id, rating }, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(botSlice.util.updateQueryData('readMyRating', id, () => rating))
      },
      invalidatesTags: (_, __, { id }) => [{ type: readOneBot, id }],
    }),
    purchaseBot: builder.mutation<void, Bot>({
      query: ({ id }) => ({
        url: `/bot/${id}/purchase`,
        method: 'POST',
      }),
      async onQueryStarted(bot, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(botSlice.util.updateQueryData('readPurchasedBots', undefined, (draft) => {
          draft.push(bot)
        }))
        dispatch(botSlice.util.updateQueryData('readOneBot', bot.id, (draft) => {
          draft.soldCount += 1
        }))
      },
    }),
  }),
})

export const {
  useReadOneBotQuery,
  useReadMyBotsQuery,
  useReadPurchasedBotsQuery,
  useReadMarketBotsInfiniteQuery,
  useCreateBotMutation,
  useRemoveBotMutation,
  useActivateBotMutation,
  useDeactivateBotMutation,
  useReadMyRatingQuery,
  useRateBotMutation,
  usePurchaseBotMutation,
} = botSlice
